import { COLORS } from "@coeff/utils";
import { Tooltip } from "antd";
import React from "react";
import styled from "styled-components";

interface CardProps {
  children?: React.ReactChildren | React.ReactChild | string | React.ReactNode;
  id?: string;
  className?: string;
  style?: object | undefined;
  showSeparator?: boolean;
  footerButtonText?: string;
  onFooterClick?: () => void;
  noMargin?: boolean;
  disabled?: boolean;
  footerButton?: React.ReactNode;
}

interface CardHeaderProps {
  children?: React.ReactChildren | React.ReactChild | string | number | boolean | React.ReactNode;
  className?: string;
  icon?: React.ReactNode;
  style?: object | undefined;
  showSeparator?: boolean;
  onClick?: () => void;
  title: string | React.ReactNode;
  color?: string;
  showTooltipOnHover?: boolean;
}

const StyledCard = styled.section<{
  noMargin?: boolean;
  disabled?: boolean;
  justifyContent?: string;
}>`
  ${({ noMargin }) => {
    if (noMargin) {
      return "";
    }

    return `
        margin: 15px;
      `;
  }}

  ${({ disabled }) => {
    if (disabled) {
      return "cursor: not-allowed;";
    }

    return "";
  }}

  ${props =>
    props.justifyContent ? `display: flex; justify-content: ${props.justifyContent};` : ""}
  background: ${COLORS.black4};
  border-radius: 5px;
  position: relative;
  transition: background-color 250ms linear;

  &.blue {
    background-color: ${COLORS.blue};
  }

  &.coeblue {
    background-color: ${COLORS.coeblue11};

    button.action {
      background: rgba(255, 255, 255, 0.7);
      border: none;
      outline: none;
      cursor: pointer;
      color: ${COLORS.coeblue4};
      padding: 8px;
      font-weight: 500;
      display: block;
      margin: 1rem auto 0px;
      border-radius: 5px;
      width: 100%;

      &:hover {
        background: rgba(255, 255, 255, 0.9);
      }
    }
  }

  &.green {
    background-color: ${COLORS.greenBg};
  }

  &.success {
    background-color: ${COLORS.greenBg2};
  }

  &.orange {
    background-color: ${COLORS.orangeBg2};
  }

  &.yellow {
    background-color: ${COLORS.yellowBg};
  }

  &.red {
    background-color: ${COLORS.redBg};
  }

  &.red2 {
    background-color: ${COLORS.redBg2};
  }

  &.purple {
    background-color: ${COLORS.magnolia};
  }

  &.magenta {
    background-color: ${COLORS.magentaBg};
  }

  &.white {
    background-color: white;

    > button:last-child {
      margin-bottom: 0px;
    }
  }

  &.transparent {
    background-color: transparent;
  }

  &.shadow {
    box-shadow: inset 0 -10px 10px -10px ${COLORS.black15};
  }

  &.noseparation {
    margin-bottom: -20px;
    padding-bottom: 5px;
  }

  &.no-vertical-margin {
    margin: 0px 15px;
  }

  &.coeff__ppress,
  &.coeff__padded {
    padding: 10px;

    .coeff__card_header {
      line-height: 1.1;
      margin-bottom: 10px;

      &.large {
        margin-bottom: 16px;
      }
    }
  }
`;

const StyledHeader = styled.div<{ color?: string }>`
  .row {
    display: flex;
    min-height: 18px;
    line-height: 20px;
    position: relative;
  }

  .sectionIcon {
    margin-right: 10px;
    width: 16px;

    svg {
      width: 100%;
      height: 100%;
    }

    .dataSourceTypeIcon {
      width: 24px;
      height: 24px;
      line-height: 24px;
    }
  }

  .title {
    flex: 1;
    min-width: 0;
    font-weight: bold;
    color: ${({ color }) => color ?? COLORS.black85};
    margin-top: 2px;
    align-items: center;
    display: flex;
  }

  &.large {
    .title {
      font-size: 18px;
      font-weight: 500;
    }

    .sectionIcon {
      margin: 0px 12px 0px 8px;
      width: 20px;
      height: 20px;

      svg {
        width: 100%;
        height: 100%;
      }
    }
  }

  &.recipe-icon .sectionIcon {
    margin-right: 10px;
    width: 40px;
  }
`;

const Separator = styled.div`
  background: white;
  height: 2px;
  width: calc(100% - 50px);
  border-radius: 4px;
  margin: 10px 25px 0px;
  position: absolute;
  bottom: 8px;
`;

const HeaderSeparator = styled.div`
  background: white;
  height: 2px;
  width: calc(100% - 50px);
  border-radius: 4px;
  margin: 10px 25px 0px;
`;

const FooterButton = styled.button`
  background: rgba(255, 255, 255, 0.7);
  border: none;
  outline: none;
  cursor: pointer;
  color: ${COLORS.coeblue4};
  padding: 8px;
  font-weight: 500;
  display: block;
  margin: auto;
  margin: 1rem auto 0px;
  border-radius: 5px;
  width: 100%;

  &:hover {
    background: rgba(255, 255, 255, 0.9);
  }
`;

export const Card: React.FC<CardProps> = ({
  children,
  className,
  showSeparator,
  footerButton,
  footerButtonText,
  onFooterClick,
  ...props
}) => {
  return (
    <StyledCard className={`coeff__card ${className}`} {...props}>
      {children}

      {showSeparator && <Separator />}

      {footerButton ? (
        footerButton
      ) : footerButtonText ? (
        <FooterButton onClick={onFooterClick}>{footerButtonText}</FooterButton>
      ) : null}
    </StyledCard>
  );
};

export const CardHeader: React.FC<CardHeaderProps> = ({
  children,
  className,
  icon,
  title,
  showSeparator,
  color,
  showTooltipOnHover,
  ...props
}) => {
  return (
    <StyledHeader className={`coeff__card_header ${className}`} color={color} {...props}>
      <div className="row">
        {icon && <div className="sectionIcon">{icon}</div>}
        <div className="title">
          {showTooltipOnHover ? (
            <Tooltip mouseEnterDelay={1} title={title}>
              {title}
            </Tooltip>
          ) : (
            title
          )}
        </div>
        {children}
      </div>
      {showSeparator && <HeaderSeparator />}
    </StyledHeader>
  );
};

export const CardContent = styled.div`
  padding: ${({ theme }) => theme.spacing(1, 1.5)};
`;

export const CardContentIndent = styled.div`
  margin-left: 26px;
  margin-bottom: 4px;
`;
